<script setup lang="ts">
import { formatDate, formatInstantDate, formatInstantTimeUtc } from '@taler/domain'
import { EmptyState, ErrorState, StatusTag } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import type { ExportJob } from '../internal/types.ts'

const props = defineProps<{
  jobs: readonly ExportJob[]
  isLoading: boolean
  error: Error | null
  retrying: boolean
  downloadingId: string | undefined
  emptyTitle?: string | undefined
  emptyBody?: string | undefined
}>()

const emit = defineEmits<{ retry: []; download: [job: ExportJob] }>()

const { t, locale } = useI18n()

const IN_PROGRESS = new Set(['QUEUED', 'RUNNING'])
</script>

<template>
  <div>
    <p v-if="isLoading" role="status" class="text-[color:var(--color-text-muted)]">
      {{ t('exports.loading') }}
    </p>

    <ErrorState
      v-else-if="error"
      :title="t('exports.errorTitle')"
      :body="t('exports.errorBody')"
      :retry-label="t('exports.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="jobs.length === 0"
      :title="props.emptyTitle ?? t('exports.emptyTitle')"
      :body="props.emptyBody ?? t('exports.emptyBody')"
    />

    <div v-else class="overflow-x-auto" tabindex="0" role="region" :aria-label="t('exports.tableRegion')">
      <table class="w-full border-collapse bg-[color:var(--color-surface)]">
        <thead>
          <tr class="table-head">
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('exports.requested') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('exports.range') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('exports.requestedBy') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('exports.status.label') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              <span class="sr-only">{{ t('exports.actions') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in jobs" :key="job.id" class="border-b border-[color:var(--color-border)]">
            <td
              class="whitespace-nowrap px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text)]"
            >
              {{ formatInstantDate(job.createdAt, locale as 'en' | 'hu') }}
              <span class="text-[color:var(--color-text-muted)]">{{
                formatInstantTimeUtc(job.createdAt)
              }}</span>
            </td>
            <td
              class="whitespace-nowrap px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text)]"
            >
              {{ formatDate(job.fromDate, locale as 'en' | 'hu') }} –
              {{ formatDate(job.untilDate, locale as 'en' | 'hu') }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text-muted)]">
              {{ job.requestedBy }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)]">
              <span
                v-if="IN_PROGRESS.has(job.status)"
                role="status"
                class="flex items-center gap-[var(--space-inline)] text-[color:var(--color-text-muted)]"
              >
                <svg
                  aria-hidden="true"
                  class="size-4 shrink-0 animate-spin text-[color:var(--color-text-muted)]"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-opacity="0.25" stroke-width="2" />
                  <path
                    d="M14 8a6 6 0 0 0-6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
                {{ t(`exports.status.${job.status}`) }}
              </span>
              <div
                v-else-if="job.status === 'FAILED'"
                class="flex flex-col items-start gap-[var(--space-tight)]"
              >
                <StatusTag tone="danger">{{ t('exports.status.FAILED') }}</StatusTag>
                <p role="alert" class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                  {{ job.errorMessage ?? t('exports.unknownError') }}
                </p>
              </div>
              <StatusTag v-else tone="ok">{{ t('exports.status.COMPLETED') }}</StatusTag>
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-right">
              <button
                v-if="job.status === 'COMPLETED'"
                type="button"
                :disabled="downloadingId === job.id"
                class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)] disabled:opacity-60"
                @click="emit('download', job)"
              >
                {{ downloadingId === job.id ? t('exports.downloading') : t('exports.download') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
